import { useState, useEffect } from 'react';
import { channelsApi } from '../services/api';
import type { Channel, AddChannelRequest } from '../types';

export function useChannels() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchChannels = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await channelsApi.getAll();
      setChannels(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch channels');
    } finally {
      setLoading(false);
    }
  };

  const addChannel = async (data: AddChannelRequest): Promise<Channel> => {
    const newChannel = await channelsApi.add(data);
    setChannels((prev) => [...prev, newChannel]);
    return newChannel;
  };

  const updateChannel = async (id: string, rating: number): Promise<void> => {
    const updated = await channelsApi.update(id, { rating });
    setChannels((prev) =>
      prev.map((channel) => (channel.id === id ? updated : channel))
    );
  };

  const deleteChannel = async (id: string): Promise<void> => {
    await channelsApi.delete(id);
    setChannels((prev) => prev.filter((channel) => channel.id !== id));
  };

  useEffect(() => {
    fetchChannels();
  }, []);

  return {
    channels,
    loading,
    error,
    addChannel,
    updateChannel,
    deleteChannel,
    refetch: fetchChannels,
  };
}
